import Class from '@/Class';
import * as interfaces from '#/CoreFrameInterfaces';
import BindingContextMixin from '@/BindingContextMixin';
import MixinHelper from '@/Utility/MixinHelper';
import Utilities from '@/Utility/Utilities';
import { EventTypes } from '#/DefaultViewExtensionInterfaces';
/**
* Dies ist eine Hilfsklasse für Control, View, Factory etc.
* Wann immer eine Klasse dazu in der Lage ist Knockout-Bindings ausführen, sollte sie einen BindingContext
* als Property haben oder hier von erben. Bei jedem ko.applyBindings(...) welches potentiell Controls, Factories etc. initialisiert, muss dieser mit übergeben werden.
* Alle so erzeugten Controls, Factories werden dann in diesem BindingContext hinterlegt, um beim Disposing des einbettenden
* Objekts mit-disposed zu werden.
*/
export default class BindingContext extends Class implements interfaces.IBindingContextMixin{

    public _registeredDisposables: Array<any>;

    /**
     * Enthält Funktionen zum Deregistrieren von Event-Handlern (werden beim Disposing ausgeführt)
     */
    public unregisterEventHandlerCallbacks: Array<() => void>;


    public constructor() {
        super();
        this._registeredDisposables = [];
        this.unregisterEventHandlerCallbacks = [];
    }

    public registerDisposable(disposable: any): void {}

    /**
     * Registers an event handler on bubbling phase
     * When element is disposed, the listener will be disposed, too
     * @param eventType
     * @param element
     * @param callback
     * @param selector
     */
    public addEventHandler<T>(eventType: EventTypes, element: JQuery<T>, callback: Function, selector: string = null): void {
        if (this.isDisposed)
            return;
        
        if (!(element instanceof jQuery))
            return;

        const eventNamespace = eventType + '.' + this.identifier;

        element.on(eventNamespace, selector, null, callback as any);
        
        this.unregisterEventHandlerCallbacks.push(() => {
            element.off(eventNamespace);
        });
    }
    
    public addNativeEventHandler<K extends keyof HTMLElementEventMap>(eventType: K, element: HTMLElement, callback: (ev: HTMLElementEventMap[K]) => any, passive: boolean = false): void {
        if (this.isDisposed)
            return;

        if (!(element instanceof HTMLElement))
            return;
        
        element.addEventListener(eventType, callback, { passive: passive });

        this.unregisterEventHandlerCallbacks.push(() => {
            element.removeEventListener(eventType, callback);
        });
    }

    /**
     * Registers an event handler on capture phase
     * When element is disposed, the listener will be disposed, too
     * @param eventType
     * @param element
     * @param callback
     */
    public addCapturedEventHandler(eventType: EventTypes, element: JQuery<HTMLElement>, callback: Function): void {
        if (this.isDisposed)
            return;
        
        if (element instanceof jQuery && element.length > 0) {
            element[0].addEventListener(eventType, callback as any, true);

            this.unregisterEventHandlerCallbacks.push(() => {
                element[0].removeEventListener(eventType, callback as any, true);
            });
        }
    }

    public removeEventHandler<T>(eventType: EventTypes, element: JQuery<T>): void {
        let eventNamespace = eventType + '.' + this.identifier;
        element.off(eventNamespace);
    }

    protected disposeInternal() {
        //  Ruft die Funktionen zum Deregistieren aller Event-Handler auf, die von diesem Control erzeugt wurden
        if (!Utilities.isNullOrUndefined(this.unregisterEventHandlerCallbacks))
            this.unregisterEventHandlerCallbacks.forEach((unreg) => {
                if (Utilities.isFunction(unreg))
                    unreg();
            });
        this.unregisterEventHandlerCallbacks = null;

        super.disposeInternal();
    }
}

MixinHelper.applyMixins(BindingContext, [BindingContextMixin]);